<template>
  <div>

    <div class="elhub-header">
      <img class="elhub-logo-image" src="../../assets/logo.svg" @click="onHeaderLogoClick">
      <span class="elhub-logo-description"></span>
      <div class="pull-right">
        <button
          title="Hjelp og forklaringer"
          class="btn btn-md btn-outlined text-primary"
          @click="onHelpBtnClick"
          style="margin-right: 6px;"
        >
          <i class="fas fa-info-circle"></i>
        </button>

        <button
          class="btn btn-md btn-outlined text-primary logout-btn"
          @click="onLogoutBtnClick"
        >
          <i class="fas fa-sign-out-alt"></i>
          Logg ut
        </button>
      </div>
    </div>

    <elhub-help-popup
      :visible.sync="showHelpPopup"
    >
    </elhub-help-popup>

    <div v-if="meterData != null">
      <div v-if="isEmployee" class="inline">
        <elhub-select-box
          v-for="filterItem in filterList"
          v-if="filterItem.data.length"
          v-on:value-change="onFilterChange"
          :item="filterItem"
          :key="filterItem.id"
        >
        </elhub-select-box>

        <div class="clear-filter-btn inline-block">
          <button
            title="Fjern alle filtervalg"
            class="btn btn-md btn-outlined text-primary"
            @click="clearFiltersClick"
          >
            <span>Tøm filter</span>
          </button>
          <span style="padding: 0 3px;"></span>
          <button
            title="Oppdater"
            :disabled="reloading"
            class="btn btn-md btn-outlined text-elhub"
            :class="{'btn-disabled': reloading}"
            @click="onReloadBtnClick"
          >
            <i
              class="fas"
              :class="{'fa-circle-notch icon-spin': reloading, 'fa-sync': !reloading}"
            >
            </i>
          </button>
        </div>

        <br>

        <div class="columns-checkbox inline-block">
          <label class="cursor-pointer">
            <span>Vis filter-kolonner:</span>
            <input
              type="checkbox"
              class="cursor-pointer"
              v-model="showFilterColumns"
            />
          </label>
        </div>
        <div class="columns-checkbox-divider inline-block"></div>
      </div>

      <div class="columns-checkbox inline-block">
        <label class="cursor-pointer">
          <span>Vis juridisk eier:</span>
          <input
            type="checkbox"
            class="cursor-pointer"
            v-model="showLegalOwner"
          />
        </label>
      </div>
    </div>

    <elhub-meter-status-grid
      v-if="meterData != null"
      :data="meterData"
      :filter="selectedFilters"
      :search-text.sync="searchValue"
      :show-filter-columns="showFilterColumns"
      :show-legal-owner="showLegalOwner"
      :event-bus="eventBus"
      @update:data="onInitDataEvent"
    >
    </elhub-meter-status-grid>

    <div v-show="meterData == null && !hasError" class="load-indicator">
      <dx-load-indicator
        height="40"
        width="40"
      />
      <h4>Laster...</h4>
    </div>

    <error-message v-show="hasError"/>
  </div>
</template>

<script>

  // External libs
  import Vue from 'vue';
  import DxButton from 'devextreme-vue/button';
  import DxCheckBox from 'devextreme-vue/check-box';
  import DxLoadIndicator from 'devextreme-vue/load-indicator';
  import {authService, queryParamService} from '@entro-no/common';

  // Internal libs
  import ElhubMeterStatusGrid from '../widgets/ElhubMeterStatusGrid';
  import ElhubSelectBox from '../widgets/ElhubSelectBox';
  import ElhubHelpPopup from '../widgets/ElhubHelpPopup';
  import ErrorMessage from '../widgets/ErrorMessage';

  // Services
  import MeterStatusService from '../services/elhub.meterstatus.service';
  import MeterFilterService from '../services/elhub.meterfilter.service';

  const enumColumnFilterNames = MeterFilterService.enumColumnFilterNames;

  function setFilterValue(value, listIndex, _this) {
    _this.selectedFilters[listIndex] = value == null ? null : [value];
  }

  function clearAllFilters (_this) {
    Object.keys(_this.selectedFilters).forEach(filterName => {
      if (filterName == null) return;
      _this.selectedFilters[filterName] = null;
    });

    _this.filterList.forEach((filterItem) => {
      filterItem.value = null;
    });

    _this.searchValue = null;

    _this.eventBus.$emit('$clearFilters');
  }

  function initData(_this) {

    MeterStatusService.getData()
      .then(res => {
        _this.meterData = res.meters;

        // TODO: Remove fallback once rollout was successful.
        if (_this.meterData === undefined) {
          _this.meterData = res;
        }

        MeterFilterService.addDataToFilterList(_this.meterData, _this.filterList);
      })
      .catch(error => {
        if (!error || error.status !== 200 || error.message === 'Failed to fetch') {
          _this.hasError = true;
        }
      })
      .finally(() => _this.reloading = false);
  }

  export default {
    name: 'ElhubPage',
    components: {
      DxButton,
      DxCheckBox,
      DxLoadIndicator,
      ElhubMeterStatusGrid,
      ElhubSelectBox,
      ElhubHelpPopup,
      ErrorMessage,
    },
    data() {
      return {
        hasError: false,
        meterData: null,
        showFilterColumns: false,
        showLegalOwner: false,
        showHelpPopup: false,
        searchValue: null,
        reloading: false,
        filterList: [
          { id: enumColumnFilterNames.EOS_CONTACT,      title: 'EOS-ansvarlig',         value: null, data: [] },
          { id: enumColumnFilterNames.CUSTOMER_CONTACT, title: 'Kundeansvarlig',        value: null, data: [] },
          { id: enumColumnFilterNames.EOS_SUPPLIER,     title: 'EOS-leverandør',        value: null, data: [] },
          { id: enumColumnFilterNames.BILLING_CONTACT,  title: 'Faktura/abb.ansvarlig', value: null, data: [] },
        ],
        selectedFilters: {
          eosContact:       null,
          customerContact:  null,
          eosSupplier:      null,
          billingContact:   null
        },
        eventBus: new Vue()
      }
    },
    computed: {
      query() {
        return queryParamService.get('q');
      },
      isEmployee() {
        return this.$user.type === 'EMPLOYEE';
      }
    },
    mounted () {
      if (this.query) this.searchValue = this.query;
      initData(this);
    },
    methods: {
      onHeaderLogoClick() {
        window.location.search = queryParamService.del('q');
      },
      onFilterChange(filter) {
        setFilterValue(filter.value, filter.id, this);
      },
      clearFiltersClick() {
        clearAllFilters(this);
      },
      onInitDataEvent() {
        initData(this);
      },
      onLogoutBtnClick() {
        authService.redirectToLogout(window.OPTIMA_AUTH_URI);
      },
      onHelpBtnClick() {
        this.showHelpPopup = true;
      },
      onReloadBtnClick() {
        this.reloading = true;
        initData(this);
      }
    }
  }
</script>

<style scoped>

  .elhub-header {
    margin-bottom:  12px;
    margin-top:     18px;
    padding-bottom: 7px;
    border-bottom:  1px solid #e4e4e4;
  }

  .elhub-logo-image {
    height: 50px;
    cursor: pointer;
  }

  .elhub-logo-description {
    font-size:   16px;
    margin-left: 0px;
  }

  .clear-filter-btn {
    vertical-align: bottom;
    top:            -3px;
    margin-top:     5px;
    position:       relative;
  }

  .columns-checkbox {
    position:  relative;
    font-size: 13px;
  }

  .columns-checkbox span {
    top:      -3px;
    position: relative;
  }

  .columns-checkbox-divider {
    margin: 0 9px;
    height: 14px;
    width: 1px;
    background-color: hsl(0, 0%, 92%);
  }

  .load-indicator {
    text-align: center;
    margin-top: 100px;
  }

  .inline {
    display: inline;
  }

  .inline-block {
    display: inline-block;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .pull-right {
    float: right;
  }
</style>

<style>
  .btn {
    display:          inline-block;
    font-size:        14px;
    font-weight:      600;
    cursor:           pointer;
    user-select:      none;
    background-color: transparent;
    outline:          none;
  }

  .btn-outlined {
    border:        1px solid gray;
    padding:       8px 18px;
    border-radius: 4px;
  }

  .btn-outlined:hover {
    color: hsl(0, 0%, 100%);
  }

  .btn-md {
    font-size:    13px;
  }

  .btn-sm {
    font-size:    12px;
  }

  .btn-xs {
    font-size:     12px;
    padding-left:  8px;
    padding-right: 8px;
    border:        1px solid;
    border-radius: 4px;
  }

  .btn-divider {
    margin:       0 7px;
    border-right: 1px solid hsl(0, 0%, 77%);
  }

  .btn.btn-disabled, .btn.btn-disabled:hover {
    color:            hsl(0, 0%, 90%) !important;
    background-color: hsl(0, 0%, 99%) !important;
    border-color:     hsl(0, 0%, 90%) !important;
    cursor:           not-allowed;
  }

  .no-margin {
    margin: 0 !important;
  }

  .no-padding {
    padding: 0 !important;
  }

  .text-elhub {
    color: hsl(146, 59%, 26%);
  }

  .btn.btn-outlined.text-elhub {
    border-color: hsl(146, 59%, 26%);
  }

  .btn.btn-outlined.text-elhub:hover {
    background-color: hsl(146, 59%, 26%);
  }

  .text-primary {
    color: hsl(207, 57%, 41%);
  }

  .btn.btn-outlined.text-primary {
    border-color: hsl(207, 57%, 41%);
  }

  .btn.btn-outlined.text-primary:hover {
    background-color: hsl(207, 57%, 41%);
  }

  .text-center {
    text-align: center;
  }

  .text-error {
    color: hsl(2, 64%, 53%);
  }

  .btn.btn-outlined.text-error {
    border-color: hsl(2, 64%, 53%);
  }

  .btn.btn-outlined.text-error:hover {
    background-color: hsl(2, 64%, 53%);
  }

  .logout-btn {
    margin-top: 5px;
  }

  .icon-spin {
    transform-origin: 50% 48%;
    animation:        spin 800ms infinite linear;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
      color:     hsl(146, 59%, 26%);
    }

    33% {
      color: hsl(281, 75%, 50%);
    }

    66% {
      color: hsl(0, 100%, 65%);
    }

    100% {
      transform: rotate(360deg);
      color:     hsl(146, 59%, 26%);
    }
  }
</style>
